import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Folder as FolderIcon, Plus } from "lucide-react";
import type { Folder } from "@/types/linguadeck";

interface MoveDecksToFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCount: number;
  folders: Folder[];
  onCreateFolder: (title: string) => Promise<Folder | null>;
  onSubmit: (folderId: string) => Promise<void>;
}

export default function MoveDecksToFolderModal({
  isOpen,
  onClose,
  selectedCount,
  folders,
  onCreateFolder,
  onSubmit,
}: MoveDecksToFolderModalProps) {
  const { t } = useTranslation();
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [newFolderTitle, setNewFolderTitle] = useState("");
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);

  const availableFolders = useMemo(
    () =>
      [...folders].sort((a, b) => {
        const aTime = a.created_at ? Date.parse(a.created_at) : 0;
        const bTime = b.created_at ? Date.parse(b.created_at) : 0;
        if (aTime !== bTime) return bTime - aTime;
        return a.title.localeCompare(b.title);
      }),
    [folders]
  );

  useEffect(() => {
    if (!isOpen) return;
    setSelectedFolderId(null);
    setIsSubmitting(false);
    setShowNewFolderInput(false);
    setNewFolderTitle("");
    setIsCreatingFolder(false);
  }, [isOpen]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleCreateFolder = async () => {
    const trimmed = newFolderTitle.trim();
    if (!trimmed || isCreatingFolder) return;
    setIsCreatingFolder(true);
    try {
      const folder = await onCreateFolder(trimmed);
      if (!folder) return;
      setShowNewFolderInput(false);
      setNewFolderTitle("");
      setSelectedFolderId(folder.id);
    } finally {
      setIsCreatingFolder(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFolderId || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onSubmit(selectedFolderId);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-md flex-col rounded-2xl border border-[hsl(var(--border))] bg-white p-6 shadow-xl dark:bg-[hsl(var(--bg2))]"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-1 shrink-0 font-[Instrument_Serif] text-xl font-semibold text-[hsl(var(--foreground))]">
          {t("folder.bulkMoveTitle")}
        </h3>
        <p className="mb-4 shrink-0 text-sm text-[hsl(var(--muted-foreground))]">
          {t("folder.selectedCount", { count: selectedCount })}
        </p>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="mb-3 shrink-0">
            {!showNewFolderInput ? (
              <button
                type="button"
                onClick={() => setShowNewFolderInput(true)}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-[hsl(var(--border))] border-dashed bg-[hsl(var(--bg3))] px-4 py-2.5 text-sm font-medium text-[hsl(var(--muted-foreground))] transition-colors hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--foreground))]"
              >
                <Plus size={18} strokeWidth={2} />
                {t("saveToFolder.newFolder")}
              </button>
            ) : (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newFolderTitle}
                  onChange={(e) => setNewFolderTitle(e.target.value)}
                  placeholder={t("folder.namePlaceholder")}
                  autoFocus
                  className="flex-1 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] px-3 py-2 text-sm text-[hsl(var(--foreground))] outline-none transition-all placeholder:text-[hsl(var(--muted-foreground))] focus:ring-2 focus:ring-[hsl(var(--primary))]"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      void handleCreateFolder();
                    }
                    if (e.key === "Escape") {
                      setShowNewFolderInput(false);
                      setNewFolderTitle("");
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => void handleCreateFolder()}
                  disabled={!newFolderTitle.trim() || isCreatingFolder}
                  className="rounded-xl bg-[hsl(var(--primary))] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isCreatingFolder ? "…" : t("folder.createFolder")}
                </button>
              </div>
            )}
          </div>

          <div className="mb-5 min-h-0 max-h-[40vh] space-y-1 overflow-y-auto pr-1 py-0.5">
            {availableFolders.length === 0 && !showNewFolderInput ? (
              <p className="py-2 text-sm text-[hsl(var(--muted-foreground))]">
                {t("folder.noOtherFolders")}
              </p>
            ) : (
              availableFolders.map((folder) => (
                <label
                  key={folder.id}
                  className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors ${
                    selectedFolderId === folder.id
                      ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 dark:bg-[hsl(var(--primary))]/15"
                      : "border-[hsl(var(--border))] hover:bg-[hsl(var(--bg3))]"
                  }`}
                >
                  <input
                    type="radio"
                    name="moveToFolder"
                    value={folder.id}
                    checked={selectedFolderId === folder.id}
                    onChange={() => setSelectedFolderId(folder.id)}
                    className="sr-only"
                  />
                  <FolderIcon size={20} className="shrink-0 text-[hsl(var(--muted-foreground))]" />
                  <span className="truncate text-sm font-medium text-[hsl(var(--foreground))]">
                    {folder.title}
                  </span>
                </label>
              ))
            )}
          </div>

          <div className="flex shrink-0 gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] px-4 py-3 text-sm font-medium text-[hsl(var(--foreground))] transition-colors hover:border-[hsl(var(--primary))]"
            >
              {t("folder.cancel")}
            </button>
            <button
              type="submit"
              disabled={!selectedFolderId || isSubmitting}
              className="flex-1 rounded-xl border-0 bg-[hsl(var(--primary))] px-4 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? "…" : t("folder.bulkMove")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
